/* eslint-disable react/no-array-index-key */
import type { BasketLineModifiedResultViewModel, ModifiedBasketViewModel } from 'autogen/swagger/all';

import Flex from 'components/Layout/components/Flex';
import { Typography } from 'components/generic/typography';
import QuickView, { useQuickView } from 'components/quickView';

import { useEffect } from 'react';
import { isEqual } from 'lodash-es';
import Card from 'components/generic/card';
import { Spacing4 } from 'autogen/design-tokens/tokens';
import ModifiedBasketItem from './components/ModifiedBasketItem';
import usePointOfSaleRequest from 'hooks/user/usePointOfSaleRequest';
import { DefaultQuickViewHeader } from 'components/quickView/components/DefaultQuickViewComponents/Header';

export default function BasketWasModifiedQuickView({
    model: modifiedBasket
}: {
    model: ModifiedBasketViewModel;
}) {
    const { isPosRequest } = usePointOfSaleRequest();
    const { modifiedBasketLines: newModifiedBasketItems, headlineText, descriptionText } = modifiedBasket;

    const {
        toggle,
        visible,
        props: { currentModifiedBasketItems }
    } = useQuickView<{ currentModifiedBasketItems?: BasketLineModifiedResultViewModel[] }>(
        'BasketWasModifiedQuickView'
    );

    const hasModifiedItems = !!newModifiedBasketItems.length;

    const hasModelChanged = () => !isEqual(currentModifiedBasketItems, newModifiedBasketItems);

    /* The Basket was modified QuickView is spawned implicitly when the backend removes items from the users basket */
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        // check if we have any incoming modified items
        if (hasModifiedItems && (!currentModifiedBasketItems?.length || hasModelChanged())) {
            // check whether we have set the currentModifiedBasketItems state if not update it with the newModifiedBasketItems
            // or if the model has changed since last fetch, if it has open the quick view and show the modifiedBasketItems
            toggle(true, { currentModifiedBasketItems: newModifiedBasketItems });
        }
    }, [hasModifiedItems, newModifiedBasketItems, toggle, visible]);

    return (
        <QuickView
            quickViewId="BasketWasModifiedQuickView"
            userCanClose={!isPosRequest}
            headerRenderer={isPosRequest ? () => null : (headerProps) => <DefaultQuickViewHeader {...headerProps} />}
        >
            <Card>
                <Card.Content>
                    <Flex.Column>
                        <Flex.Column gap={Spacing4}>
                            <Typography.Headline size="M">{headlineText}</Typography.Headline>
                            <Typography.Body as="span">{descriptionText}</Typography.Body>
                        </Flex.Column>

                        <Flex.Column>
                            {currentModifiedBasketItems?.map((modifiedBasketItem, index) => (
                                <ModifiedBasketItem key={index} model={modifiedBasketItem} />
                            ))}
                        </Flex.Column>
                    </Flex.Column>
                </Card.Content>
            </Card>
        </QuickView>
    );
}
